import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { planInfo, planComparisonFeatures, type PlanId, type PlanInfo } from '@/mocks/adminPlanPermissions';

type Step = 'select' | 'summary';

const planOrder: PlanId[] = ['free', 'pro', 'premium'];

const planIcon: Record<string, string> = {
  free: 'ri-radio-line',
  pro: 'ri-star-fill',
  premium: 'ri-vip-diamond-fill',
};

const planCardColors: Record<string, { border: string; bg: string; iconBg: string; iconColor: string; badge: string; badgeText: string; btn: string }> = {
  free: {
    border: 'border-background-200',
    bg: 'bg-background-50',
    iconBg: 'bg-secondary-100',
    iconColor: 'text-secondary-600',
    badge: 'bg-secondary-100',
    badgeText: 'text-secondary-600',
    btn: 'bg-secondary-500 hover:bg-secondary-600',
  },
  pro: {
    border: 'border-primary-300',
    bg: 'bg-primary-50/60',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-600',
    badge: 'bg-primary-100',
    badgeText: 'text-primary-600',
    btn: 'bg-primary-500 hover:bg-primary-600',
  },
  premium: {
    border: 'border-accent-300',
    bg: 'bg-accent-50/60',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
    badge: 'bg-accent-100',
    badgeText: 'text-accent-600',
    btn: 'bg-accent-500 hover:bg-accent-600',
  },
};

function upgradeSwal() {
  return Swal.mixin({
    customClass: {
      popup: 'rounded-2xl',
      title: 'font-bold text-sm',
      confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      htmlContainer: 'text-sm',
    },
    buttonsStyling: false,
  });
}

export default function InternalUpgradeModal() {
  const { isOpen, closeUpgradeModal } = useUpgradeModal();
  const { currentPlan, planId, storageUsedGB, storageTotalGB, storagePercentage } = usePlanPermissions();

  const [step, setStep] = useState<Step>('select');
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId | null>(null);
  const [includeAnnual, setIncludeAnnual] = useState(false);

  const allPlans = useMemo(() => planOrder.map((id) => planInfo[id]), []);
  const currentIndex = planOrder.indexOf(planId);
  const selectedPlan = selectedPlanId ? planInfo[selectedPlanId] : null;
  const selectedIndex = selectedPlanId ? planOrder.indexOf(selectedPlanId) : -1;
  const isUpgrade = selectedPlanId ? selectedIndex > currentIndex : false;
  const isDowngrade = selectedPlanId ? selectedIndex < currentIndex : false;

  // Check if downgrade would exceed storage
  const downgradeBlocked = useMemo(() => {
    if (!selectedPlan || !isDowngrade) return false;
    return storageUsedGB > selectedPlan.storageGB;
  }, [selectedPlan, isDowngrade, storageUsedGB]);

  // Annual license pricing (mock: 10 months = 1 year, ~17% discount)
  const monthlyPrice = useMemo(() => {
    if (!selectedPlan) return 0;
    if (selectedPlan.price === 'Gratis') return 0;
    const match = selectedPlan.price.match(/\$(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }, [selectedPlan]);

  const annualPrice = useMemo(() => Math.round(monthlyPrice * 10), [monthlyPrice]);
  const totalToday = includeAnnual ? annualPrice : monthlyPrice;

  const nextRenewal = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, []);

  const storageDiff = useMemo(() => {
    if (!selectedPlan) return '';
    const diff = selectedPlan.storageGB - storageTotalGB;
    if (diff > 0) return `+${diff} GB`;
    if (diff < 0) return `${diff} GB`;
    return 'Sin cambios';
  }, [selectedPlan, storageTotalGB]);

  // Get features difference
  const newFeatures = useMemo(() => {
    if (!selectedPlan) return [];
    const currentFeatures = planComparisonFeatures.filter((f) => {
      const currentVal = f[planId as keyof typeof f];
      return currentVal !== '—';
    });
    const selectedFeatures = planComparisonFeatures.filter((f) => {
      const selectedVal = f[selectedPlan.id as keyof typeof f];
      return selectedVal !== '—';
    });
    return selectedFeatures.filter(
      (sf) => !currentFeatures.find((cf) => cf.label === sf.label),
    );
  }, [selectedPlan, planId]);

  const lostFeatures = useMemo(() => {
    if (!selectedPlan || !isDowngrade) return [];
    const currentFeatures = planComparisonFeatures.filter((f) => {
      const currentVal = f[planId as keyof typeof f];
      return currentVal !== '—';
    });
    const selectedFeatures = planComparisonFeatures.filter((f) => {
      const selectedVal = f[selectedPlan.id as keyof typeof f];
      return selectedVal !== '—';
    });
    return currentFeatures.filter(
      (cf) => !selectedFeatures.find((sf) => sf.label === cf.label),
    );
  }, [selectedPlan, isDowngrade, planId]);

  const handleSelectPlan = useCallback((plan: PlanInfo) => {
    if (plan.id === planId) {
      upgradeSwal().fire({
        title: 'Este es tu plan actual',
        text: `Ya tenés el plan ${plan.name}. No necesitás hacer cambios.`,
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#6b7280',
      });
      return;
    }
    setSelectedPlanId(plan.id);
    setIncludeAnnual(false);
  }, [planId]);

  const handleContinueToSummary = useCallback(() => {
    if (!selectedPlanId) return;

    if (isDowngrade && downgradeBlocked) {
      upgradeSwal().fire({
        title: 'Downgrade no disponible',
        html: `<p style="font-size:13px;color:#374151;line-height:1.6">Estás usando <strong>${storageUsedGB.toFixed(1)} GB</strong> de almacenamiento, pero el plan <strong>${selectedPlan?.name}</strong> solo incluye <strong>${selectedPlan?.storageGB} GB</strong>.</p><p style="font-size:12px;color:#9ca3af;margin-top:6px">Liberá espacio eliminando archivos o elegí un plan con suficiente storage.</p>`,
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444',
      });
      return;
    }

    if (isDowngrade) {
      upgradeSwal().fire({
        title: '¿Bajar de plan?',
        html: `<div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:8px">Estás por bajar de <strong>${currentPlan.name}</strong> a <strong>${selectedPlan?.name}</strong>.</p>
          <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px;margin-bottom:8px">
            <p style="font-weight:600;color:#92400e;margin:0 0 4px;font-size:11px"><i class="ri-alert-line"></i> Importante</p>
            <p style="color:#92400e;margin:0;font-size:11px">Al bajar de plan podrías perder acceso a funciones y almacenamiento. El downgrade se aplica en la próxima renovación.</p>
          </div>
          ${lostFeatures.length > 0 ? `
            <p style="font-weight:600;color:#dc2626;font-size:11px;margin-bottom:4px">Funciones que perderías:</p>
            <ul style="list-style:none;padding:0;margin:0 0 8px">
              ${lostFeatures.map((f) => `<li style="padding:2px 0;font-size:11px;color:#dc2626"><i class="ri-close-line"></i> ${f.label}: ${f[selectedPlan!.id as keyof typeof f]}</li>`).join('')}
            </ul>
          ` : ''}
        </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, bajar de plan',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#9ca3af',
      }).then((result) => {
        if (result.isConfirmed) {
          setStep('summary');
        }
      });
      return;
    }

    setStep('summary');
  }, [selectedPlanId, isDowngrade, downgradeBlocked, selectedPlan, currentPlan, storageUsedGB, lostFeatures]);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!selectedPlan) return;

    const swal = upgradeSwal();

    // Simulate invoice generation
    await swal.fire({
      title: 'Generando invoice...',
      html: '<p style="font-size:13px;color:#6b7280">Preparando el cargo para tu nuevo plan.</p>',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    await new Promise((r) => setTimeout(r, 1500));

    Swal.close();

    const invoiceId = `INV-${Date.now().toString(36).toUpperCase()}`;
    const chargeLabel = includeAnnual
      ? `$${annualPrice.toFixed(2)} (licencia anual)`
      : `$${monthlyPrice.toFixed(2)} (mensual)`;

    const result = await swal.fire({
      title: 'Invoice generada',
      html: `
        <div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
          <p style="margin-bottom:12px">Completá el pago para activar tu nuevo plan.</p>
          <div style="background:#f3f4f6;border-radius:8px;padding:12px;margin-bottom:10px">
            <div style="display:flex;justify-content:space-between;margin-bottom:6px">
              <span style="color:#6b7280">Plan actual:</span>
              <span style="font-weight:600">${currentPlan.name}</span>
            </div>
            <div style="display:flex;justify-content:space-between;margin-bottom:6px">
              <span style="color:#6b7280">Nuevo plan:</span>
              <span style="font-weight:600;color:#f59e0b">${selectedPlan.name}</span>
            </div>
            <div style="display:flex;justify-content:space-between;margin-bottom:6px">
              <span style="color:#6b7280">Invoice:</span>
              <span style="font-weight:600;font-family:monospace;font-size:11px">${invoiceId}</span>
            </div>
            <div style="border-top:1px solid #e5e7eb;margin-top:8px;padding-top:8px;display:flex;justify-content:space-between">
              <span style="font-weight:600">Total:</span>
              <span style="font-weight:700;font-size:15px">${chargeLabel}</span>
            </div>
          </div>
          <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px;font-size:11px;color:#92400e">
            <i class="ri-information-line"></i> <strong>Upgrade pendiente de pago.</strong> El nuevo plan se activará cuando confirmes el pago.
          </div>
        </div>
      `,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Pagar ahora (mock)',
      cancelButtonText: 'Ir a Billing',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
    });

    if (result.isConfirmed) {
      // Mock payment
      await swal.fire({
        title: 'Procesando pago...',
        html: '<p style="font-size:13px;color:#6b7280">Verificando transacción...</p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await new Promise((r) => setTimeout(r, 2000));

      Swal.close();

      await swal.fire({
        title: '¡Plan activado!',
        html: `
          <div style="text-align:left;font-size:13px;color:#374151;line-height:1.6">
            <p style="margin-bottom:10px">Tu plan <strong style="color:#f59e0b">${selectedPlan.name}</strong> ya está activo.</p>
            <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:10px">
              <p style="font-weight:600;color:#166534;margin:0 0 4px;font-size:11px"><i class="ri-check-line"></i> Cambios aplicados</p>
              <ul style="list-style:none;padding:0;margin:0;font-size:11px;color:#166534">
                <li style="padding:1px 0"><i class="ri-check-line"></i> Storage: ${storageDiff}</li>
                <li style="padding:1px 0"><i class="ri-check-line"></i> Próx. renovación: ${nextRenewal}</li>
                <li style="padding:1px 0"><i class="ri-check-line"></i> Funciones desbloqueadas</li>
              </ul>
            </div>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Ir al Dashboard',
        confirmButtonColor: '#10b981',
      }).then(() => {
        closeUpgradeModal();
        setStep('select');
        setSelectedPlanId(null);
      });
    } else {
      // Go to Billing
      closeUpgradeModal();
      setStep('select');
      setSelectedPlanId(null);
    }
  }, [selectedPlan, currentPlan, includeAnnual, monthlyPrice, annualPrice, storageDiff, nextRenewal, closeUpgradeModal]);

  const handleBackToSelect = useCallback(() => {
    setStep('select');
  }, []);

  const handleClose = useCallback(() => {
    closeUpgradeModal();
    setStep('select');
    setSelectedPlanId(null);
  }, [closeUpgradeModal]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl animate-fade-in-up max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 shrink-0 border-b border-background-200">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center">
                <i className="ri-arrow-up-circle-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <h3 className="text-foreground-950 font-bold text-lg">
                  {step === 'select' ? 'Mejorar plan' : 'Confirmar cambio'}
                </h3>
                <p className="text-foreground-400 text-xs">
                  {step === 'select'
                    ? 'Elegí el plan que mejor se adapte a tu radio'
                    : 'Revisá los detalles antes de confirmar'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-xl bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
          {/* STEP: Select Plan */}
          {step === 'select' && (
            <>
              {/* Current plan banner */}
              <div className="bg-background-100 rounded-xl p-4 border border-background-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Tu plan actual</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                        planId === 'free'
                          ? 'bg-secondary-100 text-secondary-600'
                          : planId === 'pro'
                            ? 'bg-primary-100 text-primary-600'
                            : 'bg-accent-100 text-accent-600'
                      }`}>
                        {currentPlan.name}
                      </span>
                      <span className="text-foreground-500 text-xs">{currentPlan.price}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Almacenamiento</p>
                    <p className="text-foreground-800 text-sm font-bold mt-1">
                      {storageUsedGB.toFixed(1)} / {storageTotalGB} GB
                    </p>
                    <p className="text-foreground-400 text-[9px]">{storagePercentage}% usado</p>
                  </div>
                </div>
                <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      storagePercentage >= 90 ? 'bg-red-500' : storagePercentage >= 70 ? 'bg-amber-400' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Plan cards */}
              <div>
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-3">Planes disponibles</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {allPlans.map((plan) => {
                    const colors = planCardColors[plan.id];
                    const isCurrent = plan.id === planId;
                    const isSelected = plan.id === selectedPlanId;

                    return (
                      <button
                        key={plan.id}
                        onClick={() => handleSelectPlan(plan)}
                        className={`relative rounded-2xl p-5 border-2 text-left transition-all cursor-pointer ${
                          isSelected
                            ? `${colors.border} ${colors.bg} ring-1 ring-offset-1 ${plan.id === 'pro' ? 'ring-primary-300' : plan.id === 'premium' ? 'ring-accent-300' : 'ring-secondary-300'}`
                            : isCurrent
                              ? `${colors.border} ${colors.bg}`
                              : 'border-background-200 bg-background-50 hover:border-background-300 hover:bg-background-50'
                        }`}
                      >
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-xl ${colors.iconBg} flex items-center justify-center mb-3`}>
                          <i className={`${planIcon[plan.id]} ${colors.iconColor} text-base`}></i>
                        </div>

                        {/* Name & Price */}
                        <p className="text-foreground-950 font-bold text-sm mb-0.5">{plan.name}</p>
                        <p className="text-foreground-500 text-[11px] mb-3">{plan.price}{plan.price !== 'Gratis' ? '' : ''}</p>

                        {/* Storage */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center">
                            <i className="ri-hard-drive-2-line text-foreground-400 text-xs"></i>
                          </div>
                          <span className="text-foreground-700 text-xs font-semibold">{plan.storageGB} GB</span>
                        </div>

                        {/* Status badge */}
                        {isCurrent ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full ${colors.badge} ${colors.badgeText} text-[10px] font-bold`}>
                            <i className="ri-check-line text-[9px] mr-0.5"></i>
                            Plan actual
                          </span>
                        ) : isSelected ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full ${plan.id === 'pro' ? 'bg-primary-500' : plan.id === 'premium' ? 'bg-accent-500' : 'bg-secondary-500'} text-background-50 text-[10px] font-bold`}>
                            Seleccionado
                          </span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 rounded-full ${colors.badge} ${colors.badgeText} text-[10px] font-bold`}>
                            {plan.badge}
                          </span>
                        )}

                        {/* Feature preview (top 3) */}
                        <div className="mt-3 pt-3 border-t border-background-200/70 space-y-1.5">
                          {planComparisonFeatures.slice(0, 3).map((f) => {
                            const val = f[plan.id as keyof typeof f];
                            return (
                              <div key={f.label} className="flex items-center gap-2">
                                {val !== '—' ? (
                                  <i className="ri-check-line text-emerald-500 text-[10px]"></i>
                                ) : (
                                  <i className="ri-close-line text-foreground-300 text-[10px]"></i>
                                )}
                                <span className={`text-[10px] ${val !== '—' ? 'text-foreground-600' : 'text-foreground-300 line-through'}`}>
                                  {f.label}: {val}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Feature comparison table */}
              <div className="bg-background-100 rounded-xl p-4 border border-background-200">
                <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-3">
                  Qué incluye cada plan
                </p>
                <div className="space-y-2">
                  <div className="flex items-center py-1 border-b border-background-200/70">
                    <span className="flex-1 text-foreground-400 text-[10px] font-semibold"></span>
                    {allPlans.map((p) => (
                      <span key={p.id} className="w-16 text-center text-foreground-400 text-[9px] font-semibold">{p.name}</span>
                    ))}
                  </div>
                  {planComparisonFeatures.map((row) => (
                    <div key={row.label} className="flex items-center py-1.5">
                      <span className="flex-1 text-foreground-700 text-[11px] font-medium">{row.label}</span>
                      {allPlans.map((p) => {
                        const val = row[p.id as keyof typeof row];
                        const isCurr = p.id === planId;
                        const isSel = p.id === selectedPlanId;
                        return (
                          <span
                            key={p.id}
                            className={`w-16 text-center text-[11px] ${
                              val === '—'
                                ? 'text-foreground-300'
                                : isSel
                                  ? 'text-foreground-900 font-bold'
                                  : isCurr
                                    ? 'text-foreground-600 font-semibold'
                                    : 'text-foreground-600'
                            }`}
                          >
                            {val}
                          </span>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected plan quick info */}
              {selectedPlanId && selectedPlan && (
                <div className={`rounded-xl p-4 border-2 ${
                  selectedPlanId === 'pro' ? 'border-primary-300 bg-primary-50/60' :
                  selectedPlanId === 'premium' ? 'border-accent-300 bg-accent-50/60' :
                  'border-secondary-300 bg-secondary-50/60'
                }`}>
                  <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-3">
                    Resumen del cambio
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-foreground-400">Plan actual</p>
                      <p className="text-foreground-800 font-bold">{currentPlan.name} ({currentPlan.price})</p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Nuevo plan</p>
                      <p className="text-foreground-800 font-bold text-amber-600">{selectedPlan.name} ({selectedPlan.price})</p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Storage</p>
                      <p className={`text-foreground-800 font-bold ${isUpgrade ? 'text-emerald-600' : isDowngrade ? 'text-red-500' : ''}`}>
                        {storageTotalGB} GB → {selectedPlan.storageGB} GB ({storageDiff})
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-400">Precio mensual</p>
                      <p className="text-foreground-800 font-bold">
                        {monthlyPrice === 0 ? 'Gratis' : `$${monthlyPrice.toFixed(2)}`}
                      </p>
                    </div>
                    {monthlyPrice > 0 && (
                      <div>
                        <p className="text-foreground-400">Licencia anual</p>
                        <p className="text-foreground-800 font-bold">${annualPrice.toFixed(2)}/año</p>
                      </div>
                    )}
                    <div>
                      <p className="text-foreground-400">Próx. renovación</p>
                      <p className="text-foreground-800 font-bold">{nextRenewal}</p>
                    </div>
                  </div>

                  {/* New features gained */}
                  {isUpgrade && newFeatures.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70">
                      <p className="text-emerald-700 text-[10px] font-semibold mb-1.5">
                        <i className="ri-add-circle-line mr-1"></i>
                        Nuevas funciones que obtenés:
                      </p>
                      <div className="space-y-0.5">
                        {newFeatures.map((f) => (
                          <div key={f.label} className="flex items-center gap-2">
                            <i className="ri-check-line text-emerald-500 text-[10px]"></i>
                            <span className="text-emerald-700 text-[10px]">
                              {f.label}: {f[selectedPlan!.id as keyof typeof f]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isDowngrade && lostFeatures.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70">
                      <p className="text-red-600 text-[10px] font-semibold mb-1.5">
                        <i className="ri-alert-line mr-1"></i>
                        Funciones que perderías:
                      </p>
                      <div className="space-y-0.5">
                        {lostFeatures.map((f) => (
                          <div key={f.label} className="flex items-center gap-2">
                            <i className="ri-close-line text-red-400 text-[10px]"></i>
                            <span className="text-red-500 text-[10px]">{f.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Annual license toggle for paid plans */}
                  {monthlyPrice > 0 && (
                    <div className="mt-3 pt-3 border-t border-background-200/70">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeAnnual}
                          onChange={(e) => setIncludeAnnual(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-background-300 peer-checked:bg-accent-500 rounded-full peer transition-colors relative after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                        <div>
                          <p className="text-foreground-800 text-xs font-semibold">Licencia anual (10 meses)</p>
                          <p className="text-foreground-400 text-[10px]">Ahorrá ~17% — ${annualPrice.toFixed(2)} en vez de ${(monthlyPrice * 12).toFixed(2)}</p>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Downgrade warning */}
                  {isDowngrade && !downgradeBlocked && (
                    <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <div className="flex items-start gap-2">
                        <i className="ri-alert-line text-amber-600 text-sm mt-0.5 shrink-0"></i>
                        <div>
                          <p className="text-amber-800 text-[10px] font-semibold">Atención: downgrade</p>
                          <p className="text-amber-600 text-[9px] mt-0.5">
                            El downgrade se aplica en la próxima renovación. Hasta entonces mantenés tu plan actual. Podrías perder acceso a funciones y almacenamiento.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* STEP: Summary */}
          {step === 'summary' && selectedPlan && (
            <>
              {/* Back button */}
              <button
                onClick={handleBackToSelect}
                className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Volver a elegir plan
              </button>

              {/* Summary card */}
              <div className="bg-background-100 rounded-xl border border-background-200 p-5">
                <h4 className="text-foreground-950 font-bold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-amber-600"></i>
                  Resumen del cambio de plan
                </h4>

                <div className="space-y-3">
                  {/* Plan comparison */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-background-50 rounded-xl p-4 border border-background-200">
                      <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide mb-1">Plan actual</p>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          planId === 'free' ? 'bg-secondary-100' : planId === 'pro' ? 'bg-primary-100' : 'bg-accent-100'
                        }`}>
                          <i className={`${planIcon[planId]} ${planId === 'free' ? 'text-secondary-600' : planId === 'pro' ? 'text-primary-600' : 'text-accent-600'} text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-bold">{currentPlan.name}</p>
                          <p className="text-foreground-400 text-[10px]">{currentPlan.price} · {storageTotalGB} GB</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <p className="text-amber-600 text-[10px] font-semibold uppercase tracking-wide mb-1">
                        {isUpgrade ? 'Nuevo plan' : 'Nuevo plan (downgrade)'}
                      </p>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          selectedPlan.id === 'free' ? 'bg-secondary-100' : selectedPlan.id === 'pro' ? 'bg-primary-100' : 'bg-accent-100'
                        }`}>
                          <i className={`${planIcon[selectedPlan.id]} ${selectedPlan.id === 'free' ? 'text-secondary-600' : selectedPlan.id === 'pro' ? 'text-primary-600' : 'text-accent-600'} text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-foreground-800 text-sm font-bold">{selectedPlan.name}</p>
                          <p className="text-foreground-400 text-[10px]">{selectedPlan.price} · {selectedPlan.storageGB} GB</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial summary */}
                  <div className="bg-background-50 rounded-xl p-4 border border-background-200 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">
                        {includeAnnual ? 'Licencia anual' : 'Cargo inmediato'}
                      </span>
                      <span className="text-foreground-800 font-bold">
                        {monthlyPrice === 0 ? 'Gratis' : includeAnnual ? `$${annualPrice.toFixed(2)}` : `$${monthlyPrice.toFixed(2)}`}
                      </span>
                    </div>
                    {monthlyPrice > 0 && !includeAnnual && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-400">Licencia anual (opcional)</span>
                        <span className="text-foreground-500">${annualPrice.toFixed(2)}/año</span>
                      </div>
                    )}
                    <div className="border-t border-background-200 pt-2 flex items-center justify-between">
                      <span className="text-foreground-800 text-sm font-bold">Total hoy</span>
                      <span className="text-foreground-950 text-lg font-bold">
                        {totalToday === 0 ? 'Gratis' : `$${totalToday.toFixed(2)}`}
                      </span>
                    </div>
                  </div>

                  {/* What changes */}
                  <div className="bg-background-50 rounded-xl p-4 border border-background-200 space-y-2">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wide">Qué cambia</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">Storage</span>
                      <span className={`font-bold ${isUpgrade ? 'text-emerald-600' : isDowngrade ? 'text-red-500' : 'text-foreground-600'}`}>
                        {storageDiff}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">Próx. renovación</span>
                      <span className="text-foreground-600 font-medium">{nextRenewal}</span>
                    </div>
                    {isUpgrade && newFeatures.length > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-400">Nuevas funciones</span>
                        <span className="text-emerald-600 font-bold">+{newFeatures.length}</span>
                      </div>
                    )}
                    {isDowngrade && !downgradeBlocked && (
                      <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-amber-700 text-[10px]">
                          <i className="ri-information-line mr-1"></i>
                          El downgrade se aplica en la próxima renovación ({nextRenewal}).
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-background-200 flex items-center gap-3 shrink-0 bg-background-50">
          {step === 'select' ? (
            <>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleContinueToSummary}
                disabled={!selectedPlanId}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-colors whitespace-nowrap ${
                  selectedPlanId
                    ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                    : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                }`}
              >
                {selectedPlanId
                  ? isUpgrade
                    ? 'Continuar'
                    : 'Revisar downgrade'
                  : 'Seleccioná un plan'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBackToSelect}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xs mr-1.5"></i>
                Volver
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmUpgrade}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold bg-foreground-950 text-background-50 hover:bg-foreground-800 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-check-line text-sm mr-1.5"></i>
                Confirmar y generar invoice
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}